import { useState, useEffect, useRef, Suspense, useCallback } from "react";
import { useRouter } from "next/router";
import { AnimatePresence } from "framer-motion";
import { useRecoilState } from "recoil";
import parseISO from "date-fns/parseISO";
import differenceInSeconds from "date-fns/differenceInSeconds";
import MuxPlayer from "@mux/mux-player-react";
import ReactPlayer from "react-player";
import clsx from "clsx";

import {
  getGroupForTitleEngage,
  getItemNumber,
  getGroupForTitle,
} from "../../helpers/text";
import {
  getSubheadingByGroupId,
  getNameByGroupId,
  getGroupSubheadingInfo,
  getParentByGroupId,
} from "../../helpers/arrays";
import { engageState } from "../../atoms/platform";
import { apiGetVideoInfo } from "../../lib/api/engage";

import { FaRegPlayCircle } from "react-icons/fa";
import { Tab } from "@headlessui/react";

import MinutesTopicInfoMobile from "../Engage/MinutesTopicInfoMobile";
import Loading from "../Shared/Loading";
import InvalidMeeting from "../Engage/InvalidMeeting";
import AgendaForPlayer from "../Engage/AgendaForPlayer";
import VideoShareButtons from "../Engage/VideoShareButtons";
import VttChapters from "../Engage/VTTChapters";
import UserWayWidget from "../UserWayWidget";

function NoVideoPlaceholder() {
  return (
    <div className="w-full h-full bg-black flex flex-col items-center justify-center text-center px-6">
      <FaRegPlayCircle
        className="h-10 w-10 text-zinc-500 mb-3"
        aria-hidden="true"
      />
      <p className="text-zinc-200 text-sm md:text-base font-medium">
        Video not available
      </p>
      <p className="text-zinc-400 text-xs md:text-sm mt-1">
        A recording for this meeting isn&apos;t available.
      </p>
    </div>
  );
}

function Player() {
  const router = useRouter();
  const { client, id } = router.query;
  const inputRange = useRef(null);
  const ytPlayerRef = useRef(null);
  const [platform, setPlatform] = useRecoilState(engageState);
  const [agenda, setAgenda] = useState();
  const [active, setActive] = useState();
  const [mobileDetails, setMobileDetails] = useState(false);
  const [activeVideo, setActiveVideo] = useState();
  const [skipCompare, setSkipCompare] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invalidMeeting, setInvalidMeeting] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playerVersion, setPlayerVersion] = useState("mux");

  useEffect(() => {
    const update = () => setActiveVideo(window.innerWidth >= 640 ? 1 : 2);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    async function fetchVideo() {
      setLoading(true);
      const result = await apiGetVideoInfo(id);
      if (result) {
        const { meeting } = result;
        const isYouTube = meeting.videoUrl?.toLowerCase().includes("youtube");
        const isVimeo = meeting.videoUrl?.toLowerCase().includes("vimeo");
        if (isYouTube) {
          setPlayerVersion("youtube");
        } else if (isVimeo) {
          setPlayerVersion("vimeo");
        } else {
          setPlayerVersion("mux");
        }
        setPlatform(result.options);
        let dateRight;
        if (meeting.videoStartTime) {
          dateRight = parseISO(meeting.videoStartTime);
        } else {
          if (meeting.startIso) {
            dateRight = parseISO(meeting.startIso);
          }
        }
        setAgenda(meeting);
        let skips = [];
        for (const g of meeting.groupOrder) {
          const groupActions = meeting.topics
            .map((topic) => {
              // console.log(topic);
              if (topic.agendaGroup.toString() === g._id.toString()) {
                if (!topic.bulkTopic) {
                  return topic;
                } else {
                  return null;
                }
              }
              return null;
            })
            .filter((topic) => topic !== null);
          groupActions.sort((a, b) => a.order - b.order);
          for (const i of groupActions) {
            if (i.startTime) {
              if (dateRight) {
                const dateLeft = parseISO(i.startTime);
                const result = differenceInSeconds(dateLeft, dateRight, {
                  roundingMethod: "ceil",
                });
                skips.push({ seconds: result, ...i });
              }
            }
          }
        }
        setSkipCompare(skips);
        setActiveVideo(window.innerWidth >= 640 ? 1 : 2);
        setLoading(false);
      } else {
        setLoading(false);
        setInvalidMeeting(true);
      }
    }
    if (id) {
      fetchVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setPlayerRef = useCallback((player) => {
    if (!player) return;
    ytPlayerRef.current = player;
  }, []);

  const handleProgress = useCallback(
    (e) => {
      const number = Math.ceil(e.target.currentTime);
      setCurrentTime(e.target.currentTime); // add this line
      const index = skipCompare.findIndex((e) => e.seconds === number);
      if (index >= 0 && active?._id !== skipCompare[index]._id) {
        setActive(skipCompare[index]);
      }
    },
    [skipCompare, active],
  );

  // function seekTo(seconds) {
  //   if (playerVersion === "youtube" && ytPlayerRef.current) {
  //     const yt = document.querySelector("youtube-video");
  //     if (yt) yt.currentTime = seconds;
  //   } else {
  //     const player = document.querySelector("mux-player");
  //     if (player) player.currentTime = seconds;
  //   }
  // }
  function seekTo(seconds) {
    const selectors = {
      youtube: "youtube-video",
      vimeo: "vimeo-video",
      mux: "mux-player",
    };
    const el = document.querySelector(selectors[playerVersion]);
    if (el) el.currentTime = seconds;
  }

  function handleTopicChosen(topic) {
    setActive();
    if (topic.elapsedSec) {
      // inputRange.current.currentTime = topic.elapsedSec;
      seekTo(topic.elapsedSec);
      // document.querySelector("mux-player").currentTime = topic.elapsedSec;
    } else {
      const skipIndex = skipCompare.findIndex((e) => e._id === topic._id);

      if (skipIndex !== -1 && skipCompare[skipIndex]?.seconds != null) {
        // inputRange.current.currentTime = skipCompare[skipIndex].seconds;
        seekTo(skipCompare[skipIndex].seconds);
      } else {
        console.warn("No matching skipCompare entry found for topic:", topic);

        // Optional fallback — send to start
        // instead of crashing
        // inputRange.current.currentTime = 0;
        seekTo(0);
      }
    }

    setTimeout(() => {
      setActive(topic);
    }, 750);
  }

  function handleGroupChosen(elapsedSec) {
    setActive();
    // inputRange.current.currentTime = elapsedSec;
    seekTo(elapsedSec);
  }

  // const handleProgress = (state) => {
  //   const number = Math.ceil(state.playedSeconds);
  //   const index = skipCompare.findIndex((e) => e.seconds === number);
  //   // auto jump to the topic based on the ceiling of played seconds
  //   if (index >= 0) {
  //     setActive();
  //     setTimeout(() => {
  //       setActive(skipCompare[index]);
  //     }, 1000);
  //   }
  // };

  return (
    <div className="h-screen overflow-hidden bg-white">
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {active ? `Selected: ${active.name || active.title}` : ""}
      </div>
      {/* Desktop Top Banner */}
      <div className="hidden bg-slate-900 py-2 xl:py-0 xl:h-16 w-full sm:flex justify-start items-center space-x-2 xl:space-x-6 px-2 2xl:px-12">
        <p className="text-xs md:text-sm lg:text-base xl:text-lg font-medium text-white mr-5">
          {platform?.clientName}
        </p>
        <h3 className="text-mini md:text-xs lg:text-sm xl:text-lg font-medium text-center text-white">
          {agenda?.meetingType}
        </h3>
        <p className="text-mini md:text-xs lg:text-sm xl:text-lg italic text-center text-zinc-200">
          {agenda?.scheduleString}
        </p>
      </div>
      {/* Desktop Content */}
      <div className="hidden sm:grid sm:grid-cols-4 md:grid-cols-12 h-full overflow-hidden bg-white">
        {/* Main Area */}
        <div className="sm:col-span-2 md:col-span-6 lg:col-span-6 2xl:col-span-6 flex flex-col h-full overflow-hidden">
          {/* Video Player */}
          <div className="bg-black flex-shrink-0">
            <div className="aspect-video relative w-full min-h-[200px] xl:min-h-[360px]">
              {!agenda ? (
                <div className="w-full h-full bg-black" />
              ) : !agenda.videoUrl ? (
                <NoVideoPlaceholder />
              ) : playerVersion === "youtube" || playerVersion === "vimeo" ? (
                <Suspense fallback={<div className="w-full h-full bg-black" />}>
                  <ReactPlayer
                    ref={setPlayerRef}
                    src={agenda?.videoUrl}
                    controls
                    width="100%"
                    height="100%"
                    onTimeUpdate={handleProgress}
                  />
                </Suspense>
              ) : (
                <Suspense fallback={<div className="w-full h-full bg-black" />}>
                  {activeVideo && activeVideo === 1 && (
                    <MuxPlayer
                      ref={inputRange}
                      src={agenda?.videoUrl}
                      streamType="on-demand"
                      onTimeUpdate={handleProgress}
                      style={{ width: "100%", height: "100%" }}
                    />
                  )}
                </Suspense>
              )}
            </div>
          </div>
          {/* Selection Options */}

          <Tab.Group
            as="div"
            className="flex flex-col flex-1 min-h-0 bg-slate-900"
          >
            <Tab.List className="flex px-2 2xl:px-6 flex-shrink-0">
              <Tab
                className={({ selected }) =>
                  clsx(
                    "w-full py-1.5 2xl:py-2.5 text-mini xl:text-sm 2xl:text-base leading-5 font-medium text-blueGray-200 rounded-t-md",
                    "focus:outline-none",
                    selected
                      ? "bg-coolGray-700"
                      : "hover:bg-white/[0.12] hover:text-white border border-coolGray-700",
                  )
                }
              >
                Chapters
              </Tab>
              <Tab
                className={({ selected }) =>
                  clsx(
                    "w-full py-1.5 2xl:py-2.5 text-mini xl:text-sm 2xl:text-base leading-5 font-medium text-blueGray-200 rounded-t-md",
                    "focus:outline-none",
                    selected
                      ? "bg-coolGray-700"
                      : "hover:bg-white/[0.12] hover:text-white border border-coolGray-700",
                  )
                }
              >
                Share
              </Tab>
              {platform?.playerTranscript && (
                <Tab
                  className={({ selected }) =>
                    clsx(
                      "w-full py-1.5 2xl:py-2.5 text-mini xl:text-sm 2xl:text-base leading-5 font-medium text-blueGray-200 rounded-t-md",
                      "focus:outline-none",
                      selected
                        ? "bg-coolGray-700"
                        : "hover:bg-white/[0.12] hover:text-white border border-coolGray-700",
                    )
                  }
                >
                  Transcript
                </Tab>
              )}
            </Tab.List>
            <Tab.Panels className="flex-1 overflow-y-auto min-h-0">
              <Tab.Panel aria-label="Table of Contents">
                <div className="overflow-y-auto bg-slate-900 pb-sm">
                  <div className="relative mx-2 2xl:mx-6">
                    {/* Content */}
                    <div className="flex flex-col">
                      {!agenda?.archiveVideo ? (
                        <>
                          <ul className="space-y-1 overflow-y-auto">
                            {agenda?.groupOrder.map((group, gIdx) => {
                              // todo figure out if the group has elapsed seconds
                              const name = getNameByGroupId(
                                group._id,
                                agenda?.groupSubheadings,
                                group.name,
                              );
                              const groupInfo = getGroupSubheadingInfo(
                                group._id,
                                agenda?.groupSubheadings,
                              );
                              const groupActions = agenda?.topics
                                .map((topic) => {
                                  if (
                                    topic.agendaGroup._id.toString() ===
                                    group._id.toString()
                                  ) {
                                    if (!topic.bulkTopic) {
                                      return topic;
                                    } else {
                                      return null;
                                    }
                                  }
                                  return null;
                                })
                                .filter((topic) => topic !== null);
                              groupActions.sort((a, b) => a.order - b.order);
                              const allZeroOrNull = groupActions.every(
                                (topic) => !topic.elapsedSec,
                              );
                              return (
                                <div key={`group-${gIdx}`}>
                                  {groupActions.length > 0 ? (
                                    <div
                                      className={clsx(
                                        groupInfo?.elapsedSec &&
                                          "hover:bg-zinc-600 transform duration-200 cursor-pointer",
                                        allZeroOrNull &&
                                          client === "farmersbranchtx" &&
                                          groupInfo.groupId !==
                                            "67521a0f1771a90054fac31c" && // Consent Agendas
                                          "hidden",
                                        "mt-2 pl-1.5 py-0.5 rounded-md",
                                      )}
                                      onClick={() => {
                                        if (groupInfo?.elapsedSec) {
                                          handleGroupChosen(
                                            groupInfo?.elapsedSec,
                                          );
                                        }
                                      }}
                                    >
                                      <p
                                        className={clsx(
                                          groupInfo?.elapsedSec
                                            ? "text-zinc-200"
                                            : "text-zinc-300",
                                          "text-sm 2xl:text-base inline-flex items-center select-none",
                                        )}
                                      >
                                        {groupInfo?.elapsedSec !== 0 && (
                                          <FaRegPlayCircle
                                            className="h-3 w-3 mr-2 text-zinc-200"
                                            aria-hidden="true"
                                          />
                                        )}
                                        {getGroupForTitleEngage(
                                          agenda?.groupNumbering,
                                          gIdx,
                                          agenda?.groupOrder,
                                          agenda?.childNumbering,
                                        )}
                                        {name}
                                      </p>
                                    </div>
                                  ) : (
                                    <div
                                      className={clsx(
                                        groupInfo?.elapsedSec &&
                                          "hover:bg-zinc-600 transform duration-200 cursor-pointer",
                                        !groupInfo?.elapsedSec &&
                                          client === "farmersbranchtx" &&
                                          "hidden",
                                        "mt-2 pl-1.5 py-0.5 rounded-md",
                                      )}
                                      onClick={() => {
                                        if (groupInfo?.elapsedSec) {
                                          handleGroupChosen(
                                            groupInfo?.elapsedSec,
                                          );
                                        }
                                      }}
                                    >
                                      <p
                                        className={clsx(
                                          groupInfo?.elapsedSec
                                            ? "text-zinc-200"
                                            : "text-zinc-300",
                                          "text-sm 2xl:text-base inline-flex items-center select-none",
                                        )}
                                      >
                                        {groupInfo?.elapsedSec && (
                                          <FaRegPlayCircle
                                            className="h-3 w-3 mr-2 text-zinc-200"
                                            aria-hidden="true"
                                          />
                                        )}
                                        {getGroupForTitleEngage(
                                          agenda?.groupNumbering,
                                          gIdx,
                                          agenda?.groupOrder,
                                          agenda?.childNumbering,
                                        )}
                                        {name}
                                      </p>
                                    </div>
                                  )}
                                  {groupActions.map((topic, tIdx) => {
                                    if (
                                      client === "breaca" ||
                                      client === "farmersbranchtx"
                                    ) {
                                      if (topic.elapsedSec !== 0) {
                                        return (
                                          <li
                                            key={topic._id}
                                            onClick={() =>
                                              handleTopicChosen(topic)
                                            }
                                          >
                                            <div className="py-2 2xl:py-3 hover:bg-zinc-600 transform duration-200 rounded-md">
                                              <p
                                                className={clsx(
                                                  active?._id === topic._id
                                                    ? "text-white font-semibold underline"
                                                    : "text-zinc-200",
                                                  "ml-2 lg:ml-4 text-sm 2xl:text-base font-medium cursor-pointer transition duration-200 2xl:text-justify inline-flex items-center",
                                                )}
                                              >
                                                <FaRegPlayCircle
                                                  className="h-3 w-3 mr-2 text-zinc-200 flex-shrink-0"
                                                  aria-hidden="true"
                                                />
                                                {getGroupForTitle(
                                                  agenda?.groupNumbering,
                                                  gIdx,
                                                  agenda?.groupOrder,
                                                  agenda?.childNumbering,
                                                )}
                                                {getItemNumber(
                                                  agenda?.topics,
                                                  agenda?.itemNumbering,
                                                  topic,
                                                  tIdx,
                                                  agenda?.groupNumbering,
                                                  agenda?.childNumbering,
                                                )}
                                              </p>
                                            </div>
                                          </li>
                                        );
                                      } else {
                                        return null;
                                      }
                                    } else {
                                      return (
                                        <li
                                          key={topic._id}
                                          onClick={() =>
                                            handleTopicChosen(topic)
                                          }
                                        >
                                          <div className="py-2 2xl:py-3 hover:bg-zinc-600 transform duration-200 rounded-md">
                                            <p
                                              className={clsx(
                                                active?._id === topic._id
                                                  ? "text-white font-semibold underline"
                                                  : "text-zinc-200",
                                                "ml-2 lg:ml-4 text-sm 2xl:text-base font-medium cursor-pointer transition duration-200 2xl:text-justify inline-flex items-center",
                                              )}
                                            >
                                              <FaRegPlayCircle
                                                className="h-3 w-3 mr-2 text-zinc-200 flex-shrink-0"
                                                aria-hidden="true"
                                              />
                                              {getGroupForTitle(
                                                agenda?.groupNumbering,
                                                gIdx,
                                                agenda?.groupOrder,
                                                agenda?.childNumbering,
                                              )}
                                              {getItemNumber(
                                                agenda?.topics,
                                                agenda?.itemNumbering,
                                                topic,
                                                tIdx,
                                                agenda?.groupNumbering,
                                                agenda?.childNumbering,
                                              )}
                                            </p>
                                          </div>
                                        </li>
                                      );
                                    }
                                  })}
                                </div>
                              );
                            })}
                          </ul>
                          {loading && <Loading />}
                        </>
                      ) : (
                        <p className="text-white 2xl:text-xl font-medium text-center mt-8">
                          No Chapters Available
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel aria-label="Share Options">
                <div className="min-h-96 overflow-y-auto mx-2 2xl:mx-6">
                  <VideoShareButtons />
                </div>
              </Tab.Panel>
              {platform?.playerTranscript && (
                <Tab.Panel aria-label="Closed Captions">
                  <div className="min-h-96 overflow-y-auto mx-2 2xl:mx-6">
                    {/* Content */}
                    <VttChapters
                      videoUrl={agenda?.videoUrl}
                      onJump={(time) => {
                        // inputRange.current.currentTime = time;
                        seekTo(time);
                      }}
                      captionsTrack={agenda?.captionTrackId}
                      currentTime={currentTime}
                    />
                  </div>
                </Tab.Panel>
              )}
            </Tab.Panels>
          </Tab.Group>

          {/* {active && <PlayerTopicInfo topic={active?._id} client={client} />} */}
        </div>
        {/* SideBar */}
        <div className="hidden sm:block sm:col-span-2 md:col-span-6 lg:col-span-6 2xl:col-span-6 px-2 border-l border-slate-600 overflow-y-auto h-full">
          <AgendaForPlayer agenda={id} client={client} activeTopic={active} />
        </div>
      </div>

      {/* Mobile */}
      <div className="sm:hidden flex flex-col h-full overflow-y-auto">
        {/* Video Player */}
        <div className="sticky top-0 left-0 w-full h-[14rem] z-20">
          {!agenda ? (
            <div className="w-full h-full bg-black" />
          ) : !agenda.videoUrl ? (
            <NoVideoPlaceholder />
          ) : playerVersion === "youtube" || playerVersion === "vimeo" ? (
            <Suspense fallback={<div className="w-full h-full bg-black" />}>
              <ReactPlayer
                ref={setPlayerRef}
                src={agenda?.videoUrl}
                controls
                width="100%"
                height="100%"
                onTimeUpdate={handleProgress}
              />
            </Suspense>
          ) : (
            <Suspense fallback={<div className="w-full h-full bg-black" />}>
              {activeVideo && activeVideo === 2 && (
                <MuxPlayer
                  ref={inputRange}
                  src={agenda?.videoUrl}
                  streamType="on-demand"
                  onTimeUpdate={handleProgress}
                  style={{ width: "100%", height: "100%" }}
                />
              )}
            </Suspense>
          )}
        </div>

        {/* Mobile Heading */}
        <p className="text-sm md:text-base text-center font-medium text-black px-4 mt-1">
          {platform?.clientName}
        </p>
        <p className="text-xs md:text-sm text-center font-medium text-black px-4">
          {agenda?.department?.name}
        </p>
        <p className="text-xs md:text-sm text-center italic text-black px-4">
          {agenda?.scheduleString}
        </p>
        {/* Agenda  */}
        <div className="mt-4">
          {agenda?.groupOrder.map((group, index) => {
            const subheading = getSubheadingByGroupId(
              group._id,
              agenda.groupSubheadings,
            );
            const parentGroup = getParentByGroupId(
              group.id,
              agenda?.groupSubheadings,
            );
            const name = getNameByGroupId(
              group._id,
              agenda.groupSubheadings,
              group.name,
            );
            const groupInfo = getGroupSubheadingInfo(
              group._id,
              agenda.groupSubheadings,
            );
            const groupActions = agenda?.topics
              .map((topic) => {
                if (topic.agendaGroup._id.toString() === group._id.toString()) {
                  if (!topic.bulkTopic) {
                    return topic;
                  } else {
                    return null;
                  }
                }
                return null;
              })
              .filter((topic) => topic !== null);
            groupActions.sort((a, b) => a.order - b.order);
            return (
              <div
                key={index}
                className={clsx(group?.parentGroup && "ml-2", "mb-4")}
              >
                <div
                  className="border-b border-gray-800"
                  onClick={() => {
                    if (groupInfo?.elapsedSec) {
                      handleGroupChosen(groupInfo?.elapsedSec);
                    }
                  }}
                >
                  <p
                    className={clsx(
                      groupInfo?.elapsedSec
                        ? "text-blue-700 hover:text-blue-400 cursor-pointer"
                        : "text-black",
                      "px-2 text-sm font-semibold",
                    )}
                  >
                    {index + 1}. {name}
                  </p>
                </div>
                <p className="px-2 text-xs text-black italic pt-1 ml-2 whitespace-pre-line text-justify">
                  {subheading}
                </p>
                {groupActions.map((topic, i) => (
                  <div key={topic._id}>
                    <div
                      className={clsx(
                        i === 0 ? "mt-1" : "",
                        active?._id === topic._id
                          ? "text-amber-800 font-bold scale-105"
                          : "text-blue-700 hover:text-blue-400",
                        "ml-4 mb-1 transform duration-200",
                      )}
                      onClick={() => {
                        handleTopicChosen(topic);
                        // setMobileDetails(true);
                      }}
                    >
                      <p className="inline-flex text-xs font-medium">
                        {getGroupForTitle(
                          agenda?.groupNumbering,
                          index,
                          agenda?.groupOrder,
                          agenda?.childNumbering,
                        )}
                        {getItemNumber(
                          agenda?.topics,
                          agenda?.itemNumbering,
                          topic,
                          i,
                          agenda?.groupNumbering,
                          agenda?.childNumbering,
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <AnimatePresence>
          {mobileDetails && (
            <MinutesTopicInfoMobile
              show={mobileDetails}
              visible={setMobileDetails}
              topic={active?._id}
              enablePri={platform?.enablePriorities}
              client={client}
            />
          )}
        </AnimatePresence>
        <div role="status" aria-live="polite">
          {loading && <Loading />}
        </div>
      </div>
      {invalidMeeting && <InvalidMeeting />}
      <div role="status" aria-live="polite">
        {loading && <Loading />}
      </div>
      <UserWayWidget />
    </div>
  );
}

export default Player;
